import {
  ChartFacade,
  ChartId,
  TreatmentPlanFacade,
} from '@principle-theorem/ng-clinical-charting/store';
import {
  type IAppointment,
  type ITreatmentPlan,
  type ITreatmentPlanProposal,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  filterUndefined,
  sortByCreatedAt,
  type WithRef,
} from '@principle-theorem/shared';
import { BehaviorSubject, combineLatest, type Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

export class TreatmentPlanningBloc {
  private _comparisonPlanLoading$ = new BehaviorSubject<boolean>(false);
  private _comparisonPlanRef$ = new BehaviorSubject<
    DocumentReference<ITreatmentPlan> | undefined
  >(undefined);

  flaggedTreatments$: Observable<ITreatmentPlanProposal>;
  patientPlans$: Observable<WithRef<ITreatmentPlan>[]>;
  appointmentPlan$: Observable<WithRef<ITreatmentPlan> | undefined>;
  selectedPlan$: Observable<WithRef<ITreatmentPlan>>;
  comparisonPlan$: Observable<WithRef<ITreatmentPlan> | undefined>;
  comparisonPlanLoading$: Observable<boolean>;

  constructor(
    private _chartState: ChartFacade,
    private _treatmentPlanFacade: TreatmentPlanFacade,
    public appointment$: Observable<WithRef<IAppointment> | undefined> = of(
      undefined
    )
  ) {
    this.patientPlans$ = this._treatmentPlanFacade.treatmentPlans$.pipe(
      map((plans) => plans.sort(sortByCreatedAt))
    );
    this.comparisonPlanLoading$ = this._comparisonPlanLoading$.asObservable();
    this.appointmentPlan$ = this.appointment$.pipe(
      switchMap((appointment) =>
        appointment
          ? this._treatmentPlanFacade.getTreatmentPlan$(
              appointment.treatmentPlan.ref
            )
          : of(undefined)
      )
    );

    this.selectedPlan$ = combineLatest([
      this._treatmentPlanFacade.selectedTreatmentPlan$,
      this.appointmentPlan$,
    ]).pipe(
      tap(([plan, appointmentPlan]) => {
        if (!plan && appointmentPlan) {
          this._treatmentPlanFacade.selectTreatmentPlan(appointmentPlan.ref);
        }
      }),
      map(([plan]) => plan),
      filterUndefined()
    );

    this.comparisonPlan$ = this._comparisonPlanRef$.pipe(
      switchMap((ref) => {
        if (!ref) {
          this._comparisonPlanLoading$.next(false);
          return of(undefined);
        }

        this._comparisonPlanLoading$.next(true);
        return this._treatmentPlanFacade.getTreatmentPlan$(ref).pipe(
          map((plan) => plan ?? undefined),
          tap(() => this._comparisonPlanLoading$.next(false))
        );
      })
    );

    this.flaggedTreatments$ = this._chartState
      .clinicalChartState$(ChartId.InAppointment)
      .pipe(
        filterUndefined(),
        map((chart) => chart.flaggedTreatment)
      );
  }

  setComparisonPlan(plan: WithRef<ITreatmentPlan>): void {
    this._comparisonPlanRef$.next(plan.ref);
  }

  clearComparisonPlan(): void {
    this._comparisonPlanRef$.next(undefined);
  }
}
